import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import apiService from '../services/api';
import {
  DialogueReviewAnswers,
  DialogueReviewItemDto,
  DialogueReviewListItem,
  DialogueReviewProgress,
  DialogueReviewQuestionKey,
  DialogueReviewYesNo,
} from '../types';

interface QuestionConfig {
  key: DialogueReviewQuestionKey;
  number: number;
  text: string;
  help?: string[];
  // Which answer opens the turn-number field, and its label
  turnsOn: DialogueReviewYesNo;
  turnsLabel: string;
  // Order of the radio options (the rubric lists "No" first for question 6)
  options: DialogueReviewYesNo[];
}

const QUESTIONS: QuestionConfig[] = [
  {
    key: 'coherent',
    number: 1,
    text: 'Is the dialogue coherent overall?',
    turnsOn: 'no',
    turnsLabel: 'Optional: note where it breaks down',
    options: ['yes', 'no'],
  },
  {
    key: 'respondsDirectly',
    number: 2,
    text: "Do the parent turns respond directly to the clinician's statements/moves (in a “good enough” way)?",
    help: [
      '“Good enough” can mean: the response seems appropriate in context;',
      'they responded directly and completely to everything the clinician just said;',
      'they responded partially, but to something that might be considered most important;',
      'they did not respond directly, but brought up something that is understandably important to them.',
    ],
    turnsOn: 'no',
    turnsLabel: 'If no: turn # where the parent does not respond to what the clinician said',
    options: ['yes', 'no'],
  },
  {
    key: 'rewardSignal',
    number: 3,
    text: 'If the clinician used a skill, was there typically a believable reward signal from the parent?',
    help: [
      'Reward signals: clearer sharing, one direct worry or value, less suspicion, fewer challenge questions, more willingness to continue.',
    ],
    turnsOn: 'no',
    turnsLabel: 'If no: parent turn # where there was not a believable reward signal',
    options: ['yes', 'no'],
  },
  {
    key: 'challengeSignal',
    number: 4,
    text: 'If the clinician missed using a skill when important or used a pitfall, was there a believable challenge signal from the parent?',
    help: [
      'Challenge signals: repeated concern, overload, brief protest, withdrawal, mistrust, confusion, or a request to slow down.',
    ],
    turnsOn: 'no',
    turnsLabel: 'If no: parent turn # where there was not a believable challenge signal',
    options: ['yes', 'no'],
  },
  {
    key: 'soundsReal',
    number: 5,
    text: 'Does the AI parent actor sound close enough to a real parent?',
    turnsOn: 'no',
    turnsLabel: 'If no: turn # where the parent sounds not “real”',
    options: ['yes', 'no'],
  },
  {
    key: 'addsDetails',
    number: 6,
    text: "Does the AI parent actor add case details that haven't already been introduced?",
    turnsOn: 'yes',
    turnsLabel: 'If yes: turn # where this occurs',
    options: ['no', 'yes'],
  },
];

const PITFALL_TABLE: Array<{ pitfall: string; skill: string }> = [
  { pitfall: 'Misses early emotion cue, forgets to ask permission', skill: 'Asking permission to proceed; responding to emotion' },
  { pitfall: 'Forgets to assess understanding; launches into medical update', skill: 'Assessing understanding' },
  { pitfall: 'Information is too complex or detailed', skill: 'Distilling to simple prognostic information (information and meaning)' },
  { pitfall: 'Misses emotion cues, combines emotion with info, or uses only one cue', skill: 'Naming emotion, expressing understanding, showing respect, offering support, exploring' },
  { pitfall: 'Stuck in validation (spinning wheels); uses empathy without forward motion', skill: 'Asking permission to proceed' },
  { pitfall: 'Goes straight to code status', skill: 'Eliciting values' },
  { pitfall: 'Only gets one value; or the value is too broad to translate into a plan', skill: 'Eliciting more than one value; asking “what else?”' },
  { pitfall: "Explores values but can't consolidate into an alignment statement; forgets to align; or shows biased alignment", skill: 'Complex reflection; alignment' },
  { pitfall: 'Asks about what treatments they want rather than hopes/goals', skill: 'Translate values into a recommendation' },
  { pitfall: "Talks about what the medical team won't do before stating what they will do", skill: 'Talk about how care will align with values' },
];

type DraftAnswer = { answer?: DialogueReviewYesNo; turns: string; note: string };
type DraftAnswers = Record<DialogueReviewQuestionKey, DraftAnswer>;

const emptyDraft = (): DraftAnswers =>
  QUESTIONS.reduce((acc, q) => {
    acc[q.key] = { answer: undefined, turns: '', note: '' };
    return acc;
  }, {} as DraftAnswers);

const shortLabel = (item: DialogueReviewListItem): string => {
  const stage = item.title.includes('Early') ? 'Early trajectory' : 'Late trajectory';
  return `${item.childName ?? 'Case'} · ${stage}`;
};

const conditionChip = (conditionKey: string) =>
  conditionKey === 'perfect'
    ? { label: 'smooth', className: 'bg-green-100 text-green-800' }
    : { label: 'with mistakes', className: 'bg-amber-100 text-amber-800' };

const DialogueReviewPage: React.FC = () => {
  const [items, setItems] = useState<DialogueReviewListItem[]>([]);
  const [progress, setProgress] = useState<DialogueReviewProgress>({ total: 0, reviewed: 0 });
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [item, setItem] = useState<DialogueReviewItemDto | null>(null);
  const [draft, setDraft] = useState<DraftAnswers>(emptyDraft());
  const [overallComment, setOverallComment] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    try {
      const data = await apiService.getDialogueReviewItems();
      setItems(data.items);
      setProgress(data.progress);
      return data.items;
    } catch (e) {
      setError('Failed to load dialogues. Please refresh.');
      return [];
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadItem = useCallback(async (slug: string) => {
    setLoadingItem(true);
    setError(null);
    setSavedAt(null);
    try {
      const data = await apiService.getDialogueReviewItem(slug);
      setItem(data.item);
      const next = emptyDraft();
      if (data.item.existingResponse) {
        const existing = data.item.existingResponse.answers;
        for (const q of QUESTIONS) {
          const a = existing[q.key];
          if (a) {
            next[q.key] = { answer: a.answer, turns: a.turns ?? '', note: a.note ?? '' };
          }
        }
        setOverallComment(data.item.existingResponse.overallComment ?? '');
        setSavedAt(data.item.existingResponse.updatedAt);
      } else {
        setOverallComment('');
      }
      setDraft(next);
    } catch (e) {
      setError('Failed to load this dialogue.');
    } finally {
      setLoadingItem(false);
    }
  }, []);

  useEffect(() => {
    loadList().then((list) => {
      if (list.length > 0) {
        const first = list.find((i) => !i.hasResponse) ?? list[0];
        setSelectedSlug(first.slug);
      }
    });
  }, [loadList]);

  useEffect(() => {
    if (selectedSlug) {
      loadItem(selectedSlug);
    }
  }, [selectedSlug, loadItem]);

  const allAnswered = useMemo(
    () => QUESTIONS.every((q) => draft[q.key].answer !== undefined),
    [draft]
  );

  const setAnswer = (key: DialogueReviewQuestionKey, patch: Partial<DraftAnswer>) => {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSave = async () => {
    if (!item || !allAnswered) return;
    setSaving(true);
    setError(null);
    try {
      const answers = QUESTIONS.reduce((acc, q) => {
        const d = draft[q.key];
        acc[q.key] = {
          answer: d.answer as DialogueReviewYesNo,
          ...(d.turns.trim() ? { turns: d.turns.trim() } : {}),
          ...(d.note.trim() ? { note: d.note.trim() } : {}),
        };
        return acc;
      }, {} as DialogueReviewAnswers);

      await apiService.submitDialogueReviewResponse(item.slug, {
        answers,
        overallComment: overallComment.trim() || undefined,
      });
      setSavedAt(new Date().toISOString());
      await loadList();
    } catch (e) {
      setError('Failed to save your review. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dialogue Review</h1>
        <p className="mt-1 text-sm text-gray-600">
          Read each AI-generated serious illness conversation, then answer the six rubric
          questions. Both speakers are simulated: the clinician follows (or deliberately
          fumbles) the Serious Illness Conversation Guide, and the AI parent actor reacts.
          Your answers are saved per dialogue and can be revised any time.
        </p>
        <p className="mt-2 text-sm font-medium text-gray-700">
          Progress: {progress.reviewed} of {progress.total} dialogues reviewed
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px,1fr]">
        {/* Dialogue list */}
        <aside className="space-y-2">
          {loadingList && <p className="text-sm text-gray-500">Loading dialogues…</p>}
          {items.map((listItem) => {
            const chip = conditionChip(listItem.conditionKey);
            const active = listItem.slug === selectedSlug;
            return (
              <button
                key={listItem.slug}
                onClick={() => setSelectedSlug(listItem.slug)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">
                    {shortLabel(listItem)}
                  </span>
                  {listItem.hasResponse && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" title="Reviewed" />
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${chip.className}`}>
                    {chip.label}
                  </span>
                  <span className="text-xs text-gray-500">{listItem.turnCount} turns</span>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Dialogue + rubric */}
        <main>
          {loadingItem && <p className="text-sm text-gray-500">Loading dialogue…</p>}
          {!loadingItem && item && (
            <div className="space-y-6">
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h2 className="text-lg font-semibold text-gray-900">{item.title}</h2>
                <p className="mt-1 text-sm text-gray-600">{item.conditionLabel}</p>
                {item.setting && (
                  <p className="mt-2 text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Setting:</span> {item.setting}
                  </p>
                )}
                {item.situation && (
                  <p className="mt-2 rounded-md border-l-4 border-blue-500 bg-blue-50 p-3 text-sm text-gray-700">
                    <span className="font-medium">Clinical situation:</span> {item.situation}
                  </p>
                )}
                {item.sourceNote && (
                  <p className="mt-2 text-xs text-gray-400">{item.sourceNote}</p>
                )}
              </div>

              {/* Transcript */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-4 text-base font-semibold text-gray-900">
                  Transcript
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (turn numbers shown for your rubric answers)
                  </span>
                </h3>
                <div className="flex flex-col gap-3">
                  {item.turns.map((turn) => (
                    <div
                      key={turn.n}
                      className={`max-w-[85%] rounded-lg border p-3 ${
                        turn.role === 'clinician'
                          ? 'self-start border-gray-200 bg-gray-50'
                          : 'self-end border-blue-200 bg-blue-50'
                      }`}
                    >
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <span className="mr-1 font-normal text-gray-400">#{turn.n}</span>
                        {turn.speaker}
                      </div>
                      <div className="text-sm text-gray-800">{turn.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rubric form */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="text-base font-semibold text-gray-900">Rubric questions</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Revised Rubric 2.0 (May 26). Answer all six questions, then save.
                </p>

                <details className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
                  <summary className="cursor-pointer font-medium text-gray-700">
                    Reference: learner pitfalls and core skills
                  </summary>
                  <table className="mt-3 w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-300 text-gray-600">
                        <th className="py-1 pr-3 font-semibold">Learner pitfall</th>
                        <th className="py-1 font-semibold">Core skill to practice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PITFALL_TABLE.map((row, i) => (
                        <tr key={i} className="border-b border-gray-200 align-top">
                          <td className="py-1.5 pr-3 text-gray-700">{row.pitfall}</td>
                          <td className="py-1.5 text-gray-600">{row.skill}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>

                <div className="mt-4 space-y-6">
                  {QUESTIONS.map((q) => {
                    const d = draft[q.key];
                    const showTurns = d.answer === q.turnsOn;
                    return (
                      <div key={q.key} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                        <p className="text-sm font-medium text-gray-900">
                          {q.number}. {q.text}
                        </p>
                        {q.help && (
                          <ul className="mt-1 list-disc pl-5 text-xs text-gray-500">
                            {q.help.map((h, i) => (
                              <li key={i}>{h}</li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-2 flex gap-6">
                          {q.options.map((opt) => (
                            <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="radio"
                                name={`${item.slug}-${q.key}`}
                                checked={d.answer === opt}
                                onChange={() => setAnswer(q.key, { answer: opt })}
                                className="h-4 w-4 text-blue-600"
                              />
                              {opt === 'yes' ? 'Yes' : 'No'}
                            </label>
                          ))}
                        </div>
                        {showTurns && (
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-600">
                              {q.turnsLabel}
                            </label>
                            <input
                              type="text"
                              value={d.turns}
                              onChange={(e) => setAnswer(q.key, { turns: e.target.value })}
                              placeholder="e.g. 7, 11"
                              className="mt-1 w-48 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            />
                          </div>
                        )}
                        <input
                          type="text"
                          value={d.note}
                          onChange={(e) => setAnswer(q.key, { note: e.target.value })}
                          placeholder="Optional note"
                          className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-900">
                    Overall comments (optional)
                  </label>
                  <textarea
                    value={overallComment}
                    onChange={(e) => setOverallComment(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Anything else about this dialogue…"
                  />
                </div>

                {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={handleSave}
                    disabled={!allAnswered || saving}
                    className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                      allAnswered && !saving
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'cursor-not-allowed bg-gray-300'
                    }`}
                  >
                    {saving ? 'Saving…' : savedAt ? 'Update review' : 'Save review'}
                  </button>
                  {!allAnswered && (
                    <span className="text-xs text-gray-500">Answer all six questions to save.</span>
                  )}
                  {savedAt && !saving && (
                    <span className="flex items-center gap-1 text-sm text-green-700">
                      <CheckCircleIcon className="h-4 w-4" />
                      Saved {new Date(savedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DialogueReviewPage;
